/**
 * Regelversion 5: Kommunikation.
 *
 * Die Regierung hat jeden Monat einen Termin für Öffentlichkeitsarbeit (höchstens drei auf Vorrat).
 * Damit kann sie
 *  - ein Thema setzen: Vier Monate lang reden alle über dieses Problem. Hat die Regierung dazu ein
 *    Gesetz auf dem Weg oder beschlossen, zahlt sich das aus, sonst gilt es als Ankündigungspolitik.
 *    Die Opposition greift in dieser Zeit eher andere Themen an.
 *  - ein Gesetz erklären: Beim Inkrafttreten gibt es mehr positives Echo und die Gewinner merken es stärker.
 *  - einen Auftritt wählen: sachlich (beruhigt), Angriff (mobilisiert, spaltet) oder Bürgerdialog.
 * Im Sommerloch (Juli und August) wirkt jede Schlagzeile stärker, im Guten wie im Schlechten.
 * Im Mehrspieler ist das Modul aus.
 */
import { OUTLETS } from '../content/parties';
import { ISS } from '../content/states';
import { S, log } from './state';
import { clamp } from './util';
import { since } from './version';

export type Com = { pts: number; focus: { iss: string; until: number } | null };
export const COM_MAX = 3, FOCUS_MONTHS = 4;
export const newCom = (): Com => ({ pts: 1, focus: null });

export const comOn = (): boolean => since(5) && !!S.com && !S.mp;
/** Juli und August: Sommerloch. */
export const sommerloch = (m = S.m): boolean => since(5) && [6, 7].includes((11 + m) % 12);
/** Verstärkung im Sommerloch. */
export const summerK = (): number => (sommerloch() ? 1.5 : 1);

const C = (): Com => S.com as Com;
let pressFn: ((o: string, t: string, tone: number) => void) | null = null;
/** media.ts meldet seine press-Funktion an (vermeidet einen Import-Kreis). */
export function setPress(fn: (o: string, t: string, tone: number) => void) { pressFn = fn; }
const press = (o: string, t: string, tone: number) => pressFn?.(o, t, tone);

/** Hat die Regierung zu diesem Problem etwas vorzuweisen? */
export function hasAction(iss: string): boolean {
  return (S.laws || []).some((L: any) => L.iss === iss && !L.rep && !L.bestand
    && (['bt', 'br', 'vm', 'kraft'].includes(L.stage) || (L.stage === 'done' && L.doneM != null && S.m - L.doneM < 12)));
}

// ── Monatlich ──

export function comTick() {
  if (!comOn()) return;
  const c = C();
  c.pts = Math.min(COM_MAX, c.pts + 1);
  const f = c.focus;
  if (f) {
    if (S.m > f.until) { c.focus = null; return; }
    const k = summerK(), I = ISS[f.iss];
    if (hasAction(f.iss)) {
      S.press = clamp((S.press || 0) + .25 * k, -6, 6);
      for (const g in I?.imp || {}) S.bonus[g] = (S.bonus[g] || 0) + .1 * k;
    } else S.press = clamp((S.press || 0) - .15 * k, -6, 6);
  }
}

/** Beim Inkrafttreten eines erklärten Gesetzes. */
export function comOnEnact(L: any) {
  if (!comOn() || !L.pr) return;
  const k = summerK();
  press(OUTLETS[0].n, `Regierung wirbt für „${L.t}“: „Das ändert sich für Sie“`, .8 * k);
  for (const g in L.fx?.g || {}) if (L.fx.g[g] > 0) S.bonus[g] = (S.bonus[g] || 0) + L.fx.g[g] * .2 * k;
}

// ── Spielzüge ──

const spend = (): boolean => { if (!comOn() || S.phase !== 'govern' || C().pts < 1) return false; C().pts -= 1; return true; };

export function comFocus(iss: string) {
  if (!S.issues?.[iss]?.on || !ISS[iss] || C()?.focus?.iss === iss || !spend()) return;
  C().focus = { iss, until: S.m + FOCUS_MONTHS };
  const act = hasAction(iss);
  log(`Ihr setzt das Thema ${ISS[iss].n}.${act ? ' Ihr habt etwas vorzuweisen.' : ' Noch liegt kein Gesetz dazu vor, das kann als Ankündigungspolitik ankommen.'}`);
  press(OUTLETS[0].n, `Regierung rückt ${ISS[iss].n} in den Mittelpunkt`, act ? .3 * summerK() : 0);
}

export function comLaw(id: number) {
  const L: any = (S.laws || []).find((x: any) => x.id === id);
  if (!L || L.pr || L.rep || !['draft', 'ready', 'bt', 'br', 'vm', 'kraft'].includes(L.stage) || !spend()) return;
  L.pr = 1;
  if (L.stage === 'kraft') { // schon in Kraft: sofort erklären
    const k = summerK();
    press(OUTLETS[0].n, `Regierung erklärt „${L.t}“`, .5 * k);
    for (const g in L.fx?.g || {}) if (L.fx.g[g] > 0) S.bonus[g] = (S.bonus[g] || 0) + L.fx.g[g] * .1 * k;
  } else log(`Ihr bereitet eine Kampagne für „${L.t}“ vor. Sie startet, wenn das Gesetz in Kraft tritt.`);
}

export const TALKS: Record<string, [string, string]> = {
  sachlich: ['Sachlich erklären', 'Ruhiger Auftritt, etwas positives Echo, die Spaltung im Land sinkt.'],
  angriff: ['Opposition angreifen', 'Mobilisiert die eigene Basis, spaltet aber und ärgert den Koalitionspartner.'],
  dialog: ['Bürgerdialog vor Ort', 'Wenig Schlagzeilen, aber alle Gruppen fühlen sich gehört.'],
};
export function comTalk(kind: string) {
  if (!TALKS[kind] || !spend()) return;
  const k = summerK();
  if (kind === 'sachlich') { press(OUTLETS[1].n, 'Kanzleramt erklärt Regierungskurs ruhig und sachlich', .4 * k); if ((S as any).polar) (S as any).polar = Math.max(0, (S as any).polar - 3); }
  if (kind === 'angriff') {
    press(OUTLETS[3].n, 'Scharfer Schlagabtausch: Kanzleramt geht die Opposition frontal an', .2 * k);
    S.basis += .4 * k; (S as any).polar = ((S as any).polar || 0) + 4;
    if (S.partners.length) S.coal = clamp(S.coal - 2, 0, 100);
  }
  if (kind === 'dialog') { for (const g in S.bonus) S.bonus[g] += .1 * k; log('Bürgerdialog: Ihr hört zu, was die Menschen vor Ort bewegt.'); }
}
